/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical';
import '../utils/LayoutNode.css'

import { addClassNamesToElement } from '@lexical/utils';
import { $createParagraphNode, $createTextNode, $getRoot, $getSelection, $isElementNode, $isRangeSelection, ElementNode, ParagraphNode } from 'lexical';

let count = 1
let defaultVal = "Type a Header"
let defaultFooter = "Type a Footer"

export function setDefaultHeader(val: string) {
  defaultVal = val
}

export function setDefaultFooter(val: string) {
  defaultFooter = val
}

function generateID(): string {
  const now = new Date();

  const hours = now.getHours();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();

  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  count++;

  return `${count}-${String(totalSeconds).padStart(6, '0')}`;
}

export function getHeight(node: LayoutItemNode): number | null {
  const element = document.getElementById(node.getId());
  if (element) {
    return element.offsetHeight;
  }
  return null;
}



export function getSelectedPage(): LayoutItemNode | null {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    return null;
  }

  let node: any = selection.anchor.getNode();

  while (node !== null) {
    if ($isLayoutItemNode(node)) {
      return node;
    }
    node = node.getParent();
  }

  return null;
}

export function getLastPage(): LayoutItemNode | null {
  const root = $getRoot();
  let lastLayoutNode: LayoutItemNode | null = null;

  function traverse(node: LexicalNode) {
    if ($isLayoutItemNode(node)) {
      lastLayoutNode = node;
    }

    if ($isElementNode(node)) {
      node.getChildren().forEach(traverse);
    }
  }

  root.getChildren().forEach(traverse);

  return lastLayoutNode;
}


export function getAllLayoutItemNodes(): LayoutItemNode[] {
  const layoutNodes: LayoutItemNode[] = [];

  $getRoot().getChildren().forEach(function traverse(node) {
    if ($isLayoutItemNode(node)) {
      layoutNodes.push(node);
    }

    if ($isElementNode(node)) {
      node.getChildren().forEach(traverse);
    }
  });

  return layoutNodes;
}


export type SerializedLayoutItemNode = Spread<{
  id: string;
  className: string
  headerText: string;
  footerText: string;
}, SerializedElementNode>;

export class LayoutItemNode extends ElementNode {
  __className: string;
  __id: string;
  __headerText: string;
  __footerText: string;

  static getType(): string {
    return 'layout-item';
  }

  setClassName(className: string): this {
    const writable = this.getWritable();
    writable.__className = className;
    return this;
  }
  getClassName(): string | undefined{
    return this.__className
  }
  removeClassName(className: string): this {
    const writable = this.getWritable();
    if (writable.__className === className) {
      writable.__className = "";
    }
    return this;
  }

  setHeaderText(text: string): this {
    const writable = this.getWritable();
    writable.__headerText = text;
    return this;
  }

  getHeaderText(): string {
  return this.__headerText;
}

  getFooterText(): string {
  return this.__footerText;
}


  setFooterText(text: string): this {
    const writable = this.getWritable();
    writable.__footerText = text;
    return this;
  }

  static clone(node: LayoutItemNode): LayoutItemNode {
    return new LayoutItemNode(node.__className, node.__id, node.__headerText, node.__footerText, node.__key);
  }

  constructor(className: string, id?: string, headerText?: string, footerText?: string, key?: NodeKey) {
    super(key);
    this.__className = className || 'custom-div';
    this.__id = id ?? generateID();
    this.__headerText = headerText ?? defaultVal;
    this.__footerText = footerText ?? defaultFooter;
  }

  getId(): string {
    return this.__id;
  }

prepend(node: LexicalNode): this {
  const writable = this.getWritable();
  const firstChild = writable.getFirstChild();
  
  if (node.getParent() === null) {
    // Node is detached; just append it safely
    writable.append(node);
  } else {
    if (firstChild) {
      writable.insertBefore(node);
    } else {
      writable.append(node);
    }
  }
  
  return this;
}




  createDOM(config: EditorConfig): HTMLElement {
    const dom = document.createElement('div');
    // dom.innerHTML = `<input id="${this.__id}-header" type="text" class="Header-Input" value="${this.__headerText}">     <input id="${this.__id}-footer" type="text" class="Footer-Input" value="${this.__footerText}">`
    dom.id = this.__id
    count = count + 1
    dom.classList.add("custom-div");
    dom.style.border = "1px solid #aaa";
    dom.style.padding = "8px";
    dom.style.minHeight = "1000px";
    dom.style.maxWidth = '600px'
    dom.style.padding = '96px 96px'
    dom.style.marginLeft = '17.5%'

    dom.setAttribute('HeaderText', this.__headerText)
    dom.setAttribute('FooterText', this.__footerText)


      const headerInput = document.createElement('input');
  headerInput.type = 'text';
  headerInput.maxLength = 80;
  headerInput.className = 'Header-Input';
  headerInput.value = this.__headerText;
  headerInput.id = `${this.__id}-header`;
  headerInput.setAttribute('contentEditable', 'false');
  headerInput.setAttribute('title','Double click to edit')
  headerInput.disabled = true
  headerInput.setAttribute("data-lexical-ignore", "true");

  const headerDiv = document.createElement('div')
  headerDiv.className = 'header-container';
  headerDiv.id = `${this.__id}-header-container`
  headerDiv.append(headerInput)

  // Create footer input
  const footerInput = document.createElement('input');
  footerInput.type = 'text';
  footerInput.maxLength = 80;
  footerInput.className = 'Footer-Input';
  footerInput.value = this.__footerText;
  footerInput.id = `${this.__id}-footer`;
  footerInput.setAttribute('contentEditable', 'false');
  footerInput.setAttribute('title','Double click to edit')
  footerInput.disabled = true
  footerInput.setAttribute("data-lexical-ignore", "true");

   const footerDiv = document.createElement('div')
  footerDiv.className = 'footer-container';
  footerDiv.id = `${this.__id}-footer-container`
  footerDiv.append(footerInput)

  const paragraph = document.createElement('p')
  paragraph.className = "PlaygroundEditorTheme__paragraph";
  const br = document.createElement("br");
  paragraph.appendChild(br);

  dom.appendChild(headerDiv);
  dom.appendChild(footerDiv);
  dom.appendChild(paragraph)


    if (this.__className) {
      dom.classList.add(this.__className);
    }
    
    const page = getSelectedPage()






    const headerInputs = dom.querySelectorAll('.Header-Input');
const footerInputs = dom.querySelectorAll('.Footer-Input');

setTimeout(()=>{
  const currentHeaderCon = document.getElementById(`${this.__id}-header-container`)
const curHeaderIn = document.getElementById(`${this.__id}-header`)
currentHeaderCon?.addEventListener('dblclick',()=>{
  (curHeaderIn as HTMLInputElement).disabled = false
    const event = new CustomEvent('InputDoubleClicked', {
      detail: {
        id: (curHeaderIn as HTMLInputElement).id,
        type: 'header',
      },
      bubbles: true, // Allow it to bubble up to parent if needed
    });
    (curHeaderIn as HTMLInputElement).dispatchEvent(event);  
})

const currentFooterCon = document.getElementById(`${this.__id}-footer-container`)
const currentFooterIn = document.getElementById(`${this.__id}-footer`);

  if(currentFooterIn){
    (currentFooterCon as HTMLInputElement).addEventListener('mouseenter', (e: MouseEvent) => {
      (currentFooterCon?.firstChild as HTMLInputElement).disabled = false;
      (currentFooterCon?.firstChild as HTMLInputElement).readOnly = true
      console.log('mouse entered')
        e.preventDefault();
        e.stopPropagation();
      });

          (currentFooterCon as HTMLInputElement).addEventListener('mouseleave', (e: MouseEvent) => {
      (currentFooterCon?.firstChild as HTMLInputElement).disabled = true;
      (currentFooterCon?.firstChild as HTMLInputElement).readOnly = false
        e.preventDefault();
        e.stopPropagation();
      });

          (currentFooterIn as HTMLInputElement).addEventListener('mousedown', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      });
  }

currentFooterCon?.addEventListener('dblclick',(e)=>{
          e.preventDefault();
        e.stopPropagation();
  (currentFooterIn as HTMLInputElement).readOnly = false;
      const event = new CustomEvent('InputDoubleClicked', {
      detail: {
        id: (currentFooterIn as HTMLInputElement).id,
        type: 'footer',
      },
      bubbles: true,
    });
    (currentFooterIn as HTMLInputElement).dispatchEvent(event);
})
},1000)




    return dom;
  }

  updateDOM(prevNode: LayoutItemNode, dom: HTMLElement): boolean {
    let didUpdate = false;

    // Handle class name changes
    if (this.__className !== prevNode.__className) {
      if (prevNode.__className) {
        dom.classList.remove(prevNode.__className);
      }
      if (this.__className) {
        dom.classList.add(this.__className);
      }
      didUpdate = true;
    }
       if (this.__headerText !== prevNode.__headerText) {
      if (prevNode.__headerText) {
        dom.setAttribute('data-header',``)
      }
      if (this.__headerText) {
        dom.setAttribute('data-header',`${this.__headerText.length > 0 ? this.__headerText : 'Type a Header'}`)
      }
      didUpdate = true;
    }
    
if (this.getChildren().length === 0) {
  this.append($createParagraphNode().append($createTextNode("")));
  didUpdate = true
}

           if (this.__footerText !== prevNode.__footerText) {
      if (prevNode.__footerText) {
        dom.setAttribute('data-footer',``)
      }
      if (this.__footerText) {
        dom.setAttribute('data-footer',`${this.__footerText.length > 0 ? this.__footerText : 'Type a Footer'}`)
      }
      didUpdate = true;
    }

      if (!dom.querySelector(`input.Header-Input`)) {
    const div = document.createElement('div')
    const headerInput = document.createElement('input');
    headerInput.type = 'text';
    headerInput.className = 'Header-Input';
    headerInput.value = this.__headerText.length > 0 ? this.__headerText : 'Type a Header';
    headerInput.id = `${this.__id}-header`;
    headerInput.setAttribute('contentEditable', 'false');
    div.append(headerInput)
    dom.prepend(div);
    didUpdate = true;
  }

  if (!dom.querySelector(`input.Footer-Input`)) {
    const div = document.createElement('div')
    const footerInput = document.createElement('input');
    footerInput.type = 'text';
    footerInput.className = 'Footer-Input';
    footerInput.value = this.__footerText.length > 0 ? this.__footerText : 'Type a Footer';
    footerInput.id = `${this.__id}-footer`;
    footerInput.setAttribute('contentEditable', 'false');
    div.append(footerInput)
    dom.appendChild(div);
    didUpdate = true;
  }


    return didUpdate;
  }



  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (domNode.classList.contains('PlaygroundEditorTheme__layoutItem') || domNode.hasAttribute('data-lexical-layout-item') || domNode.classList.contains('custom-div') ) {
          return {
            conversion: convertLayoutItemElement,
            priority: 2,
          };
        }
        return null;
      },
    };
  }


  static importJSON(SerializedLayoutItemNode: SerializedLayoutItemNode): LayoutItemNode {
    return new LayoutItemNode(
      SerializedLayoutItemNode.className || 'custom-div',
      SerializedLayoutItemNode.id,
      SerializedLayoutItemNode.headerText,
      SerializedLayoutItemNode.footerText,);
  }

  isShadowRoot(): boolean {
    return true;
  }

  exportJSON(): SerializedLayoutItemNode {
    return {
      ...super.exportJSON(),
      type: 'layout-item',
      version: 1,
      id: this.__id,
      className: this.__className,
      headerText: this.__headerText,
      footerText: this.__footerText,
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');

    element.innerHTML = `<input id="${this.__id}-header" data-lexical-ignore="true" type="text" class="Header-Input" value="${this.__headerText}">     <input id="${this.__id}-footer" data-lexical-ignore="true" type="text" class="Footer-Input" value="${this.__footerText}">`;

    element.classList.add("custom-div");
    element.style.padding = "8px";
    element.style.minHeight = "1100px";
    element.style.maxWidth = '600px'
    element.style.padding = '96px 96px'
    element.style.marginLeft = '17.5%'
    element.id = this.__id

    // ✅ Add the stored class name
    if (this.__className) {
      element.classList.add(this.__className);
      // Optional: Also add it as an attribute for easier re-import
      element.setAttribute("data-classname", this.__className);
    }
    element.setAttribute("data-header", this.__headerText);
    element.setAttribute("data-footer", this.__footerText);

    element.setAttribute("data-lexical-layout-item", "true"); // mark it as your custom node
    return { element };
  }

}

export function $createLayoutItemNode(className: string): LayoutItemNode {
  return new LayoutItemNode(className);
}

export function $isLayoutItemNode(
  node: LexicalNode | null | undefined,
): node is LayoutItemNode {
  return node instanceof LayoutItemNode;
}

function convertLayoutItemElement(domNode: HTMLElement): DOMConversionOutput | null {
  const className = domNode.getAttribute('data-classname')
  const headerText = domNode.getAttribute('data-header') || defaultVal;
  const footerText = domNode.getAttribute('data-footer') || defaultFooter;
  return {
    node: new LayoutItemNode(className || 'custom-div', undefined, headerText, footerText),
  };
}
